import React, { useMemo, useState } from 'react';
import { Empty, Table } from 'antd';
import { useI18n } from '../../i18n';
import WizardStepFilters from './WizardStepFilters';
import { buildServicesStepColumns } from './wizardStepColumns';
import { toCatalogItemRef, hasSupplierConnection, filterCatalogRows } from '../../utils/wizardHelpers';

/**
 * SelectionWizard step 2 (inquiry-module-redesign follow-up, Requirement 6):
 * search and multi-select from the Products Module's own Services catalog
 * (`initialServiceItems`) — not the Supplier_Product catalog Products uses,
 * and not scoped to the item's category (a decoration service isn't tied to
 * one product category the way a product is). Columns/search mirror the
 * Products Module's Services tab, plus the connected-supplier badge column:
 * a service is "connected" if its supplier is also the supplier of one of
 * the products selected in step 1. Filters by Service Type instead of
 * Status — every catalog entry here is already selectable. Supplier is
 * clickable — opens the Supplier Profile page and returns here after.
 */
const SERVICE_TYPE_OPTIONS = [
  { value: 'Decoration', label: 'Decoration' },
  { value: 'Charge', label: 'Charge' },
];

export default function ServicesStep({
  services = [],
  suppliers = [],
  selectedProducts = [],
  selectedServices = [],
  onChange,
  onOpenSupplier,
}) {
  const { t } = useI18n();
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');

  const rows = useMemo(() => services.map((service) => toCatalogItemRef(service, suppliers)), [services, suppliers]);

  const filtered = useMemo(() => (
    filterCatalogRows(rows, search).filter((row) => typeFilter === 'all' || row.serviceType === typeFilter)
  ), [rows, search, typeFilter]);

  const selectedKeys = selectedServices.map((ref) => ref.supplierProductId);

  // rowSelection.onChange reports the full selected set for the CURRENTLY
  // VISIBLE (filtered) rows only — rows hidden by search/type filters
  // keep whatever selection state they already had.
  const handleSelectionChange = (_keys, newlySelectedVisibleRows) => {
    const visibleKeys = new Set(filtered.map((row) => row.supplierProductId));
    const kept = selectedServices.filter((ref) => !visibleKeys.has(ref.supplierProductId));
    onChange([...kept, ...newlySelectedVisibleRows]);
  };

  const handleOpenSupplier = (row) => {
    const supplier = suppliers.find((s) => s.id === row.supplierId);
    if (supplier) onOpenSupplier?.(supplier);
  };

  const columns = buildServicesStepColumns({
    t,
    isConnected: (record) => hasSupplierConnection(record.supplierId, selectedProducts),
    onOpenSupplier: handleOpenSupplier,
  });

  return (
    <div>
      <WizardStepFilters
        t={t}
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder={t('Search by code or service name')}
        typeFilter={typeFilter}
        onTypeFilterChange={setTypeFilter}
        typeOptions={SERVICE_TYPE_OPTIONS}
      />
      {rows.length === 0 ? (
        <Empty description={t('No services available for this category')} />
      ) : filtered.length === 0 ? (
        <Empty description={t("No results for '{{query}}'", { query: search })} />
      ) : (
        <Table
          rowKey="supplierProductId"
          columns={columns}
          dataSource={filtered}
          pagination={false}
          size="middle"
          bordered
          scroll={{ x: 'max-content' }}
          rowSelection={{
            selectedRowKeys: selectedKeys,
            onChange: handleSelectionChange,
          }}
        />
      )}
    </div>
  );
}
